import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  Ban,
  Building2,
  CheckCircle2,
  Clock,
  DoorOpen,
  ExternalLink,
  Frown,
  KeyRound,
  MapPin,
  Meh,
  Pencil,
  Smile,
} from "lucide-react";
import { PROPERTY, PROPERTY_MAPS_URL } from "@/lib/property";
import {
  ApiError,
  api,
  type Door,
  type Feedback,
  type FeedbackRating,
  type GuestView,
  type RegistrationResponse,
} from "@/lib/api";
import { formatCountdownParts, formatDayTime, formatRange } from "@/lib/time";
import { useLanguage } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LanguageToggle } from "@/components/LanguageToggle";

// Code-split: the registration form (and its day-picker + country/phone
// pickers + OCR scaffolding) is only needed when there are documents left
// to fill in. Door-only guests skip the download entirely.
const RegistrationWizard = lazy(() =>
  import("@/components/RegistrationWizard").then((m) => ({
    default: m.RegistrationWizard,
  }))
);

type State =
  | { kind: "loading" }
  | { kind: "ready"; data: GuestView }
  | { kind: "notFound" }
  | { kind: "error" };

export default function Guest() {
  const { token } = useParams<{ token: string }>();
  const { t } = useLanguage();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [now, setNow] = useState(() => Date.now());
  const [pressing, setPressing] = useState<Door | null>(null);
  // When the guest opens the wizard via "Edit my details", we keep showing
  // the form even after the snapshot says we're complete.
  const [editMode, setEditMode] = useState(false);
  // Tracks the previous `registrationComplete` so we can detect the moment it
  // flips from false to true (e.g. another phone just filled the last slot
  // for us) and exit editMode automatically -- otherwise the wizard would
  // stay open even though the registration is now finished.
  const wasCompleteRef = useRef(false);
  // Same idea for `lockedForEdit`: when the cron (or an inline submit from
  // another tab) reports the data to the police mid-edit, the poll picks
  // up the new locked state and we need to kick the wizard closed so the
  // guest sees the "already sent" message instead of an editor that can no
  // longer save.
  const wasLockedRef = useRef(false);

  const load = useCallback(async (tok: string) => {
    try {
      const data = await api.guest.get(tok);
      setState({ kind: "ready", data });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setState({ kind: "notFound" });
      } else {
        setState({ kind: "error" });
      }
    }
  }, []);

  const onRegistrationUpdate = useCallback((res: RegistrationResponse) => {
    setState((s) =>
      s.kind === "ready"
        ? {
            kind: "ready",
            data: {
              ...s.data,
              stayGuestCount: res.stayGuestCount,
              registration: res.registration,
              registrationComplete: res.registrationComplete,
              lockedForEdit: res.lockedForEdit ?? s.data.lockedForEdit,
            },
          }
        : s
    );
  }, []);

  useEffect(() => {
    if (!token) return;
    load(token);
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [token, load]);

  // When registration just became complete (false -> true), drop out of
  // editMode so the wizard unmounts and the user sees the completed view.
  useEffect(() => {
    if (state.kind !== "ready") return;
    const isComplete = state.data.registrationComplete;
    const wasComplete = wasCompleteRef.current;
    wasCompleteRef.current = isComplete;
    if (!wasComplete && isComplete) {
      setEditMode(false);
    }
  }, [state]);

  // When the data flips from editable to locked (data was just sent to
  // the police while the guest was editing), close the wizard and tell
  // them what happened.
  useEffect(() => {
    if (state.kind !== "ready") return;
    const isLocked = state.data.lockedForEdit === true;
    const wasLocked = wasLockedRef.current;
    wasLockedRef.current = isLocked;
    if (!wasLocked && isLocked && editMode) {
      setEditMode(false);
      toast.info(t.regLockedDesc);
    }
  }, [state, editMode, t]);

  // Warm the wizard chunk in the background once we know the guest can
  // still reach it (not revoked, not past checkout). If they tap "Edit my
  // details" later, the bundle is already on disk and the wizard mounts
  // synchronously.
  useEffect(() => {
    if (state.kind !== "ready") return;
    const d = state.data;
    if (d.revoked) return;
    if (Date.now() > d.validUntil) return;
    void import("@/components/RegistrationWizard");
  }, [state]);

  // Poll the registration snapshot while a guest could still be editing it
  // (scheduled state, link not revoked). Lets multiple phones see each other's
  // updates within a few seconds without hammering the main GET endpoint
  // (which records a `viewed` event each call).
  useEffect(() => {
    if (state.kind !== "ready" || !token) return;
    const data = state.data;
    if (data.revoked) return;
    const stayStarted = Date.now() >= data.validFrom;
    if (stayStarted) return;
    if (data.registrationComplete && !editMode) return;
    const id = setInterval(async () => {
      try {
        const res = await api.guest.getRegistration(token);
        onRegistrationUpdate(res);
      } catch {
        // Polling is best-effort; ignore transient failures.
      }
    }, 3000);
    return () => clearInterval(id);
  }, [
    state,
    token,
    onRegistrationUpdate,
    editMode,
  ]);

  async function open(door: Door) {
    if (!token || pressing) return;
    setPressing(door);
    try {
      await api.guest.open(token, door);
      toast.success(door === "apartment" ? t.apartmentOpened : t.portalOpened);
    } catch (err) {
      const message =
        err instanceof ApiError ? errorMessage(err, t) : t.couldNotOpenDoor;
      toast.error(message);
      if (token) await load(token);
    } finally {
      setPressing(null);
    }
  }

  const submitFeedback = useCallback(
    async (input: { rating: FeedbackRating; comment?: string }) => {
      if (!token) throw new Error("no_token");
      const res = await api.guest.submitFeedback(token, input);
      setState((s) =>
        s.kind === "ready"
          ? { kind: "ready", data: { ...s.data, feedback: res.feedback } }
          : s
      );
      return res.feedback;
    },
    [token]
  );

  const recordBookingClick = useCallback(async () => {
    if (!token) return;
    try {
      await api.guest.recordBookingReviewClick(token);
    } catch {
      // Click is opportunistic — failures shouldn't block the redirect.
    }
  }, [token]);

  return (
    <>
      <div
        className="fixed z-50"
        style={{
          top: "max(0.75rem, env(safe-area-inset-top))",
          right: "max(0.75rem, env(safe-area-inset-right))",
        }}
      >
        <LanguageToggle />
      </div>
      <GuestBody
        token={token ?? ""}
        state={state}
        now={now}
        pressing={pressing}
        editMode={editMode}
        onOpen={open}
        onSubmitFeedback={submitFeedback}
        onBookingReviewClick={recordBookingClick}
        onRegistrationUpdate={onRegistrationUpdate}
        onStartEdit={() => setEditMode(true)}
        onFinishEdit={() => setEditMode(false)}
      />
    </>
  );
}

function GuestBody({
  token,
  state,
  now,
  pressing,
  editMode,
  onOpen,
  onSubmitFeedback,
  onBookingReviewClick,
  onRegistrationUpdate,
  onStartEdit,
  onFinishEdit,
}: {
  token: string;
  state: State;
  now: number;
  pressing: Door | null;
  editMode: boolean;
  onOpen: (door: Door) => void;
  onSubmitFeedback: (input: {
    rating: FeedbackRating;
    comment?: string;
  }) => Promise<Feedback>;
  onBookingReviewClick: () => Promise<void>;
  onRegistrationUpdate: (res: RegistrationResponse) => void;
  onStartEdit: () => void;
  onFinishEdit: () => void;
}) {
  const { t } = useLanguage();
  const [confirmDoor, setConfirmDoor] = useState<Door | null>(null);
  // While the dialog plays its close animation, `confirmDoor` is already null
  // and the ternaries below would briefly render the other door's content.
  // Keep the last opened door around so the dialog body stays stable during
  // the fade-out.
  const lastConfirmDoor = useRef<Door | null>(null);
  if (confirmDoor) lastConfirmDoor.current = confirmDoor;
  const dialogDoor = confirmDoor ?? lastConfirmDoor.current;

  if (state.kind === "loading") return <GuestSkeleton />;
  if (state.kind === "notFound") {
    return (
      <Centered>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t.linkNotFound}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {t.linkNotFoundDesc}
        </p>
      </Centered>
    );
  }
  if (state.kind === "error") {
    return (
      <Centered>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t.somethingWentWrong}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">{t.tryAgain}</p>
      </Centered>
    );
  }

  const { data } = state;
  // Effective lock-out moment: validUntil plus the configured grace window
  // when the grace feature is enabled. During the grace gap the link is
  // still openable -- the UI calls it out with the dedicated banner below.
  const effectiveValidUntil = data.gracePeriodEnabled
    ? data.validUntil + data.gracePeriodMinutes * 60_000
    : data.validUntil;
  const scheduled = !data.revoked && now < data.validFrom;
  const pastCheckout = !data.revoked && now > data.validUntil;
  const inGrace = pastCheckout && now <= effectiveValidUntil;
  const fullyExpired = !data.revoked && now > effectiveValidUntil;
  const terminalReason = nonScheduledReason(data, now, effectiveValidUntil, t);
  const disabled = scheduled || !!terminalReason;
  const greeting = data.revoked
    ? t.greetingRevoked(data.guestName)
    : scheduled
      ? t.greetingScheduled(data.guestName)
      : fullyExpired
        ? t.greetingExpired(data.guestName)
        : t.greetingActive(data.guestName);

  // Gate the page behind registration during scheduled + active. Once the stay
  // ended or the link is revoked, the gate disappears so feedback / revoked
  // cards still show. The grace window counts as "after the stay" for this
  // purpose -- the guest is on their way out, not registering. The admin can
  // also opt this booking out of documents entirely from the detail page, in
  // which case there is nothing to fill in and the wizard never appears.
  const needsRegistration =
    !data.revoked &&
    !pastCheckout &&
    data.documentsRequired &&
    !data.registrationComplete;
  const showWizard = needsRegistration || editMode;

  return (
    <>
      <main
        className="min-h-full flex flex-col items-center justify-center px-5 py-8 gap-6 sm:gap-8"
        style={{
          paddingTop: "max(2rem, env(safe-area-inset-top))",
          paddingBottom: "max(2rem, env(safe-area-inset-bottom))",
        }}
      >
        <header className="text-center w-full max-w-sm">
          <DoorOpen className="h-8 w-8 mx-auto text-muted-foreground" />
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-3 break-words">
            {greeting}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {formatRange(data.validFrom, data.validUntil)}
          </p>
          {!disabled && !showWizard && !inGrace && (
            <p className="mt-3 text-sm text-muted-foreground">
              {t.checkoutLabel}:{" "}
              <span className="font-semibold text-foreground">
                {formatDayTime(data.validUntil)}
              </span>
            </p>
          )}
        </header>

        {showWizard ? (
          <Suspense fallback={<WizardChunkSkeleton />}>
            <RegistrationWizard
              token={token}
              stayGuestCount={data.stayGuestCount}
              snapshot={data.registration}
              onUpdate={onRegistrationUpdate}
              initialStepHint={editMode ? "auto" : "intro"}
            />
          </Suspense>
        ) : (
          <>
            {scheduled && (
              <ScheduledCountdownCard
                msUntilOpen={data.validFrom - now}
                opensAt={data.validFrom}
              />
            )}

            {inGrace && (
              <GracePeriodCard
                msUntilLockOut={effectiveValidUntil - now}
                expiresAt={effectiveValidUntil}
              />
            )}

            {data.revoked ? (
              <TerminalStateCard
                icon={<Ban className="size-7" />}
                title={t.linkRevokedTitle}
                description={t.linkRevokedDesc}
                tone="danger"
              />
            ) : !scheduled && pastCheckout ? (
              <FeedbackCard
                feedback={data.feedback}
                bookingReviewUrl={data.bookingReviewUrl}
                onSubmit={onSubmitFeedback}
                onBookingClick={onBookingReviewClick}
              />
            ) : null}

            {!disabled && (
              <div className="w-full max-w-sm grid grid-cols-2 gap-3">
                <BigDoorButton
                  icon={<Building2 className="size-7" />}
                  label={t.openPortalDoor}
                  pressing={pressing === "portal"}
                  disabled={pressing !== null}
                  onClick={() => setConfirmDoor("portal")}
                  iconBadgeClassName="bg-blue-500/10 text-blue-600 ring-blue-500/15 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-400/20"
                  emphasised={inGrace}
                />
                <BigDoorButton
                  icon={<KeyRound className="size-7" />}
                  label={t.openApartmentDoor}
                  pressing={pressing === "apartment"}
                  disabled={pressing !== null}
                  onClick={() => setConfirmDoor("apartment")}
                  iconBadgeClassName="bg-emerald-500/10 text-emerald-600 ring-emerald-500/15 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-400/20"
                  emphasised={inGrace}
                />
              </div>
            )}

            {scheduled && data.registrationComplete && (
              <CompletedRegistrationCard
                onEdit={onStartEdit}
                locked={data.lockedForEdit === true}
              />
            )}

            {!terminalReason && !inGrace && <GettingHereCard />}
          </>
        )}

        {showWizard && editMode && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onFinishEdit}
            className="text-muted-foreground"
          >
            {t.cancel}
          </Button>
        )}
      </main>

      <AlertDialog
        open={confirmDoor !== null}
        onOpenChange={(o) => !o && setConfirmDoor(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {dialogDoor === "apartment"
                ? t.confirmOpenApartmentTitle
                : t.confirmOpenPortalTitle}
            </AlertDialogTitle>
            {dialogDoor === "apartment" ? (
              <>
                <div className="flex items-center justify-center gap-1.5 text-amber-600 dark:text-amber-400 sm:justify-start">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span className="text-sm font-semibold">
                    {t.confirmApartmentWarning}
                  </span>
                </div>
                <AlertDialogDescription>
                  {t.confirmOpenApartmentDesc}
                </AlertDialogDescription>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center gap-1.5 text-rose-600 dark:text-rose-400 sm:justify-start">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span className="text-sm font-semibold">
                    {t.closePortalWarning}
                  </span>
                </div>
                <AlertDialogDescription>
                  {t.confirmOpenPortalDesc}
                </AlertDialogDescription>
              </>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                const door = confirmDoor;
                setConfirmDoor(null);
                if (door) onOpen(door);
              }}
            >
              {t.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function nonScheduledReason(
  data: GuestView,
  now: number,
  effectiveValidUntil: number,
  t: ReturnType<typeof useLanguage>["t"]
): string | null {
  if (data.revoked) return t.linkRevoked;
  // Only counts as fully expired once the grace window (if any) has
  // elapsed; the door routes use the same window.
  if (now > effectiveValidUntil) return t.linkExpired;
  return null;
}

function ScheduledCountdownCard({
  msUntilOpen,
  opensAt,
}: {
  msUntilOpen: number;
  opensAt: number;
}) {
  const { t } = useLanguage();
  const { d, h, m, s } = formatCountdownParts(msUntilOpen);
  return (
    <div className="w-full max-w-sm rounded-2xl border border-blue-500/20 bg-blue-500/[0.06] p-6 shadow-sm dark:bg-blue-500/[0.09]">
      <div className="flex items-center justify-center gap-2">
        <span className="relative flex h-2 w-2 items-center justify-center">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500/60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700 dark:text-blue-300">
          {t.accessOpensInLabel}
        </span>
      </div>
      <div className="mt-5 grid grid-cols-4 gap-2 text-center">
        <CountdownCell value={d} label={t.unitDays} />
        <CountdownCell value={h} label={t.unitHours} />
        <CountdownCell value={m} label={t.unitMinutes} />
        <CountdownCell value={s} label={t.unitSeconds} />
      </div>
      <p className="mt-5 text-center text-xs text-muted-foreground">
        {t.opensAtLabel}{" "}
        <span className="font-medium text-foreground">
          {formatDayTime(opensAt)}
        </span>
      </p>
    </div>
  );
}

// Shown only during the post-checkout grace window (validUntil <= now <=
// validUntil + gracePeriodMinutes). Mirrors the shape of the scheduled
// countdown card but in amber, with a single mm:ss countdown -- the
// remaining window is short by design so we drop the days/hours cells.
function GracePeriodCard({
  msUntilLockOut,
  expiresAt,
}: {
  msUntilLockOut: number;
  expiresAt: number;
}) {
  const { t } = useLanguage();
  const totalSeconds = Math.max(0, Math.floor(msUntilLockOut / 1000));
  const mm = Math.floor(totalSeconds / 60);
  const ss = totalSeconds % 60;
  return (
    <div className="w-full max-w-sm rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-6 shadow-sm dark:border-amber-400/25 dark:bg-amber-500/[0.09]">
      <div className="flex items-center justify-center gap-2">
        <Clock className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300">
          {t.graceTitle}
        </span>
      </div>
      <div className="mt-5 flex items-end justify-center gap-2 tabular-nums">
        <span className="text-5xl font-semibold leading-none tracking-tight text-foreground">
          {String(mm).padStart(2, "0")}
        </span>
        <span className="text-3xl font-semibold leading-none text-muted-foreground pb-1">
          :
        </span>
        <span className="text-5xl font-semibold leading-none tracking-tight text-foreground">
          {String(ss).padStart(2, "0")}
        </span>
      </div>
      <p className="mt-3 text-center text-xs text-muted-foreground">
        {t.graceDesc}
      </p>
      <p className="mt-3 text-center text-[11px] text-muted-foreground">
        {t.graceEndsAtLabel}{" "}
        <span className="font-medium text-foreground">
          {formatDayTime(expiresAt)}
        </span>
      </p>
    </div>
  );
}

function TerminalStateCard({
  icon,
  title,
  description,
  tone,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  tone: "neutral" | "danger";
}) {
  const isDanger = tone === "danger";
  return (
    <div
      className={cn(
        "w-full max-w-sm rounded-2xl border p-6 text-center shadow-sm",
        isDanger
          ? "border-rose-500/20 bg-rose-500/[0.06] dark:bg-rose-500/[0.09]"
          : "border-border bg-muted/40"
      )}
    >
      <span
        className={cn(
          "inline-flex h-14 w-14 items-center justify-center rounded-2xl ring-1 ring-inset",
          isDanger
            ? "bg-rose-500/10 text-rose-600 ring-rose-500/15 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-400/20"
            : "bg-background text-muted-foreground ring-border"
        )}
      >
        {icon}
      </span>
      <h2 className="mt-4 text-lg font-semibold tracking-tight">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function CountdownCell({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <div className="w-full rounded-xl bg-card px-1 py-3 text-2xl font-semibold tabular-nums tracking-tight shadow-sm sm:text-3xl">
        {String(value).padStart(2, "0")}
      </div>
      <span className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

function errorMessage(
  err: ApiError,
  t: ReturnType<typeof useLanguage>["t"]
): string {
  switch (err.message) {
    case "revoked":
      return t.linkRevoked;
    case "expired":
      return t.linkExpired;
    case "not_yet_active":
      return t.linkNotYetActive;
    case "not_registered":
      return t.errNotRegistered;
    case "door_failed":
      return t.doorNotResponded;
    default:
      return t.couldNotOpenDoor;
  }
}

function CompletedRegistrationCard({
  onEdit,
  locked,
}: {
  onEdit: () => void;
  locked: boolean;
}) {
  const { t } = useLanguage();
  return (
    <div className="w-full max-w-sm rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.05] p-5 shadow-sm dark:bg-emerald-500/[0.08]">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 ring-1 ring-inset ring-emerald-500/15 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-400/20">
          <CheckCircle2 className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold tracking-tight">
            {t.regCompletedTitle}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {locked ? t.regLockedDesc : t.regCompletedDesc}
          </p>
        </div>
      </div>
      {!locked && (
        <Button
          variant="outline"
          size="sm"
          className="mt-3 w-full"
          onClick={onEdit}
        >
          <Pencil className="size-3.5" />
          {t.regEditMyData}
        </Button>
      )}
    </div>
  );
}

function GettingHereCard() {
  const { t } = useLanguage();
  return (
    <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-center gap-2">
        <MapPin className="h-4 w-4 text-muted-foreground" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {t.gettingHereTitle}
        </span>
      </div>

      <button
        type="button"
        aria-label={t.gettingHereOpenInMaps}
        onClick={() => window.open(PROPERTY_MAPS_URL, "_blank", "noopener")}
        className="mt-3 flex w-full items-center gap-3 rounded-xl border border-border bg-secondary/50 px-3 py-2.5 text-left transition-colors hover:bg-accent/60"
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">
            {PROPERTY.addressLine1}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {PROPERTY.addressLine2}
          </p>
        </div>
        <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <div className="flex flex-col items-center rounded-xl bg-secondary py-4 px-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            {t.gettingHereApartmentLabel}
          </span>
          <div className="mt-2 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-card ring-1 ring-inset ring-border">
            <span className="text-2xl font-bold tracking-tight tabular-nums">
              {PROPERTY.apartment}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-center rounded-xl bg-secondary py-4 px-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            {t.gettingHereElevatorLabel}
          </span>
          <div className="mt-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-foreground text-background ring-4 ring-foreground/10">
            <span className="text-2xl font-bold tabular-nums">
              {PROPERTY.elevatorFloor}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-500/10 px-3 py-2.5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <p className="text-xs leading-relaxed text-foreground">
          {t.gettingHereTip(PROPERTY.elevatorFloor, PROPERTY.apartment)}
        </p>
      </div>
    </div>
  );
}

function FeedbackCard({
  feedback,
  bookingReviewUrl,
  onSubmit,
  onBookingClick,
}: {
  feedback: Feedback | null;
  bookingReviewUrl: string;
  onSubmit: (input: {
    rating: FeedbackRating;
    comment?: string;
  }) => Promise<Feedback>;
  onBookingClick: () => Promise<void>;
}) {
  const { t } = useLanguage();
  const [editing, setEditing] = useState(!feedback);
  const [selectedRating, setSelectedRating] = useState<FeedbackRating | null>(
    feedback?.rating ?? null
  );
  const [commentValue, setCommentValue] = useState(feedback?.comment ?? "");
  const [submitting, setSubmitting] = useState(false);

  // If the server-side feedback changes (e.g. after a fresh load), sync local
  // form state so the rating + comment shown match the saved value.
  useEffect(() => {
    if (feedback) {
      setSelectedRating(feedback.rating);
      setCommentValue(feedback.comment ?? "");
    }
  }, [feedback]);

  async function submitRating(rating: FeedbackRating, comment: string) {
    if (submitting) return;
    setSubmitting(true);
    try {
      const trimmed = comment.trim();
      const next = await onSubmit({
        rating,
        comment:
          rating === "happy" || trimmed.length === 0 ? undefined : trimmed,
      });
      setSelectedRating(next.rating);
      setCommentValue(next.comment ?? "");
      setEditing(false);
    } catch {
      toast.error(t.feedbackSaveError);
    } finally {
      setSubmitting(false);
    }
  }

  function pickRating(rating: FeedbackRating) {
    setSelectedRating(rating);
    // Happy needs no extra context, so skip the Send step and submit straight
    // into the Booking nudge.
    if (rating === "happy") {
      void submitRating("happy", "");
    }
  }

  async function handleBookingClick() {
    onBookingClick();
    window.open(bookingReviewUrl, "_blank", "noopener");
  }

  if (!editing && feedback) {
    return (
      <FeedbackThanksCard
        feedback={feedback}
        onChange={() => setEditing(true)}
        onBookingClick={handleBookingClick}
      />
    );
  }

  return (
    <FeedbackPromptCard
      selectedRating={selectedRating}
      onPickRating={pickRating}
      commentValue={commentValue}
      onCommentChange={setCommentValue}
      onSubmit={() => {
        if (!selectedRating) return;
        void submitRating(selectedRating, commentValue);
      }}
      submitting={submitting}
      canCancel={!!feedback}
      onCancel={() => {
        if (feedback) {
          setSelectedRating(feedback.rating);
          setCommentValue(feedback.comment ?? "");
        }
        setEditing(false);
      }}
    />
  );
}

function FeedbackPromptCard({
  selectedRating,
  onPickRating,
  commentValue,
  onCommentChange,
  onSubmit,
  submitting,
  canCancel,
  onCancel,
}: {
  selectedRating: FeedbackRating | null;
  onPickRating: (r: FeedbackRating) => void;
  commentValue: string;
  onCommentChange: (v: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  canCancel: boolean;
  onCancel: () => void;
}) {
  const { t } = useLanguage();
  const wantsComment =
    selectedRating === "sad" || selectedRating === "neutral";

  return (
    <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-sm">
      <h2 className="text-center text-lg font-semibold tracking-tight">
        {t.feedbackPromptTitle}
      </h2>
      <p className="mt-1 text-center text-sm text-muted-foreground">
        {t.feedbackPromptDesc}
      </p>

      <div className="mt-5 grid grid-cols-3 gap-2">
        <RatingButton
          icon={<Frown className="size-7" />}
          label={t.feedbackRatingSadLabel}
          selected={selectedRating === "sad"}
          disabled={submitting}
          onClick={() => onPickRating("sad")}
          badgeClassName="bg-rose-500/10 text-rose-600 ring-rose-500/15 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-400/20"
          selectedRingClassName="ring-rose-500/60 dark:ring-rose-400/50"
        />
        <RatingButton
          icon={<Meh className="size-7" />}
          label={t.feedbackRatingNeutralLabel}
          selected={selectedRating === "neutral"}
          disabled={submitting}
          onClick={() => onPickRating("neutral")}
          badgeClassName="bg-amber-500/10 text-amber-600 ring-amber-500/15 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-400/20"
          selectedRingClassName="ring-amber-500/60 dark:ring-amber-400/50"
        />
        <RatingButton
          icon={<Smile className="size-7" />}
          label={t.feedbackRatingHappyLabel}
          selected={selectedRating === "happy"}
          disabled={submitting}
          onClick={() => onPickRating("happy")}
          badgeClassName="bg-emerald-500/10 text-emerald-600 ring-emerald-500/15 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-400/20"
          selectedRingClassName="ring-emerald-500/60 dark:ring-emerald-400/50"
        />
      </div>

      {wantsComment && (
        <div className="mt-5 space-y-2">
          <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {t.feedbackCommentLabel}
          </label>
          <textarea
            value={commentValue}
            onChange={(e) => onCommentChange(e.target.value)}
            placeholder={t.feedbackCommentPlaceholder}
            rows={3}
            maxLength={1000}
            className="w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
          />
        </div>
      )}

      {wantsComment && (
        <div className="mt-5 flex flex-col gap-2">
          <Button
            size="lg"
            disabled={submitting}
            onClick={onSubmit}
          >
            {submitting ? t.feedbackSubmitting : t.feedbackSubmit}
          </Button>
          {canCancel && (
            <Button
              size="sm"
              variant="ghost"
              disabled={submitting}
              onClick={onCancel}
            >
              {t.cancel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function RatingButton({
  icon,
  label,
  selected,
  disabled,
  onClick,
  badgeClassName,
  selectedRingClassName,
}: {
  icon: React.ReactNode;
  label: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  badgeClassName: string;
  selectedRingClassName: string;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="lg"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "h-28 flex-col items-center justify-center gap-2 px-1 transition-all",
        selected ? cn("ring-2", selectedRingClassName) : "ring-0"
      )}
    >
      <span
        className={cn(
          "inline-flex h-12 w-12 items-center justify-center rounded-2xl ring-1 ring-inset",
          badgeClassName
        )}
      >
        {icon}
      </span>
      <span className="text-xs font-medium leading-tight text-center whitespace-normal">
        {label}
      </span>
    </Button>
  );
}

function FeedbackThanksCard({
  feedback,
  onChange,
  onBookingClick,
}: {
  feedback: Feedback;
  onChange: () => void;
  onBookingClick: () => void;
}) {
  const { t } = useLanguage();
  const isHappy = feedback.rating === "happy";
  const Icon = isHappy ? Smile : feedback.rating === "sad" ? Frown : Meh;
  const badgeClassName = isHappy
    ? "bg-emerald-500/10 text-emerald-600 ring-emerald-500/15 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-400/20"
    : feedback.rating === "sad"
      ? "bg-rose-500/10 text-rose-600 ring-rose-500/15 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-400/20"
      : "bg-amber-500/10 text-amber-600 ring-amber-500/15 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-400/20";

  const description = isHappy
    ? t.feedbackHappyDesc
    : feedback.rating === "sad"
      ? t.feedbackThanksDescSad
      : t.feedbackThanksDescNeutral;
  const title = isHappy ? t.feedbackHappyTitle : t.feedbackThanksTitle;

  return (
    <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
      <span
        className={cn(
          "inline-flex h-14 w-14 items-center justify-center rounded-2xl ring-1 ring-inset",
          badgeClassName
        )}
      >
        <Icon className="size-7" />
      </span>
      <h2 className="mt-4 text-lg font-semibold tracking-tight">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{description}</p>

      {isHappy && (
        <Button
          size="lg"
          className="mt-5 w-full bg-[#003580] text-white hover:bg-[#003580]/90 dark:bg-[#0071c2] dark:hover:bg-[#0071c2]/90"
          onClick={onBookingClick}
        >
          <ExternalLink className="mr-1 size-4" />
          {t.feedbackBookingButton}
        </Button>
      )}

      {isHappy && feedback.bookingReviewClickedAt && (
        <p className="mt-3 text-xs text-emerald-600 dark:text-emerald-400">
          {t.feedbackBookingThanks}
        </p>
      )}

      <button
        type="button"
        className="mt-4 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        onClick={onChange}
      >
        {t.feedbackChangeRating}
      </button>
    </div>
  );
}

function BigDoorButton({
  icon,
  label,
  pressing,
  disabled,
  onClick,
  iconBadgeClassName,
  emphasised,
}: {
  icon: React.ReactNode;
  label: string;
  pressing: boolean;
  disabled: boolean;
  onClick: () => void;
  iconBadgeClassName?: string;
  // Adds an amber outline + tint to signal this is overtime access during
  // the grace window. Per-door colour on the icon badge stays the same so
  // portal vs apartment is still distinguishable at a glance.
  emphasised?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <Button
      size="lg"
      variant="outline"
      className={cn(
        "h-32 flex-col items-center justify-center gap-3 px-3 transition-colors",
        emphasised &&
          "border-amber-500/40 bg-amber-500/[0.04] ring-2 ring-amber-500/30 hover:bg-amber-500/[0.08] dark:border-amber-400/30 dark:bg-amber-500/[0.06] dark:ring-amber-400/25"
      )}
      disabled={disabled}
      onClick={onClick}
    >
      <span
        className={cn(
          "inline-flex h-14 w-14 items-center justify-center rounded-2xl ring-1 ring-inset",
          iconBadgeClassName
        )}
      >
        {icon}
      </span>
      <span className="text-sm font-semibold leading-tight text-center whitespace-normal">
        {pressing ? t.opening : label}
      </span>
    </Button>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-full flex items-center justify-center px-6 text-center">
      <div className="max-w-sm">{children}</div>
    </main>
  );
}

function WizardChunkSkeleton() {
  return (
    <section className="w-full max-w-sm space-y-3 rounded-lg border bg-card p-4">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-2 w-full rounded-full" />
      <div className="space-y-2 pt-2">
        <Skeleton className="h-10 rounded-md" />
        <Skeleton className="h-10 rounded-md" />
        <Skeleton className="h-10 rounded-md" />
      </div>
      <Skeleton className="h-11 w-full rounded-md" />
    </section>
  );
}

function GuestSkeleton() {
  return (
    <main
      className="min-h-full flex flex-col items-center justify-center px-5 py-8 gap-6 sm:gap-8"
      style={{
        paddingTop: "max(2rem, env(safe-area-inset-top))",
        paddingBottom: "max(2rem, env(safe-area-inset-bottom))",
      }}
    >
      <header className="text-center w-full max-w-sm flex flex-col items-center gap-3">
        <Skeleton className="h-8 w-8 rounded-md" />
        <Skeleton className="h-7 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </header>
      <div className="w-full max-w-sm grid grid-cols-2 gap-3">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-32 rounded-xl" />
      </div>
    </main>
  );
}
